import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAppForm } from "@/components/ui/form";
import { forgetPassword } from "@/lib/auth-client";
import * as m from "@/paraglide/messages";

import { PreserverSearchLink } from "../preserver-search-link";

type ForgotPasswordFormValues = {
	email: string;
};

const defaultValues: ForgotPasswordFormValues = {
	email: "",
};

export function ForgotPasswordForm({ ...props }: React.ComponentProps<typeof Card>) {
	const [emailSent, setEmailSent] = useState(false);

	const forgotPasswordSchema = z.object({
		email: z.email(m.auth_emailInvalid()),
	});

	const forgotPasswordMutation = useMutation({
		mutationKey: ["auth", "forgot-password"],
		mutationFn: async (values: ForgotPasswordFormValues) => {
			const result = await forgetPassword({
				email: values.email,
				redirectTo: "/reset-password",
			});

			if (result.error) {
				throw new Error(result.error.message ?? m.auth_resetEmailFailed());
			}

			return result.data;
		},
		meta: {
			success: { message: m.auth_resetEmailSuccess() },
			error: { message: m.auth_resetEmailFailed() },
		},
		onSuccess: () => {
			setEmailSent(true);
		},
	});

	const form = useAppForm({
		validators: { onChange: forgotPasswordSchema },
		defaultValues,
		onSubmit: ({ value }) => {
			forgotPasswordMutation.mutate(value);
		},
	});

	const handleSubmit = useCallback(
		(e: React.SubmitEvent) => {
			e.preventDefault();
			e.stopPropagation();
			form.handleSubmit();
		},
		[form],
	);

	if (emailSent) {
		return (
			<Card {...props}>
				<CardHeader>
					<CardTitle>{m.auth_checkEmailTitle()}</CardTitle>
					<CardDescription>{m.auth_checkEmailDesc()}</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="flex flex-col gap-4">
						<p className="text-muted-foreground text-sm">{m.auth_noEmailReceived()}</p>
						<Button variant="outline" onClick={() => setEmailSent(false)}>
							{m.auth_tryAgain()}
						</Button>
						<PreserverSearchLink
							to="/sign-in"
							className="text-center text-sm underline underline-offset-4"
						>
							{m.auth_backToSignIn()}
						</PreserverSearchLink>
					</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card {...props}>
			<CardHeader>
				<CardTitle>{m.auth_forgotPasswordTitle()}</CardTitle>
				<CardDescription>{m.auth_forgotPasswordDesc()}</CardDescription>
			</CardHeader>
			<CardContent>
				<form.AppForm>
					<form className="space-y-4" onSubmit={handleSubmit}>
						<form.AppField name="email">
							{(field) => (
								<field.TextField
									label={m.auth_email()}
									type="email"
									placeholder="m@example.com"
									autoComplete="email"
									required
								/>
							)}
						</form.AppField>
						<form.SubmitButton
							className="w-full"
							disabled={forgotPasswordMutation.isPending}
						>
							{forgotPasswordMutation.isPending
								? m.auth_sending()
								: m.auth_sendResetLink()}
						</form.SubmitButton>
						<p className="text-muted-foreground text-center text-sm">
							{m.auth_rememberPassword()}{" "}
							<Link to="/sign-in" className="underline underline-offset-4">
								{m.auth_signIn()}
							</Link>
						</p>
					</form>
				</form.AppForm>
			</CardContent>
		</Card>
	);
}
